import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconChevronDown } from '@tabler/icons-react'
import { ReactComponent as ArrowLeftIcon } from '@assets/images/Icons/Arrow_2_left.svg'
import { ReactComponent as ArrowRightIcon } from '@assets/images/Icons/Arrow_2_right.svg'
import { ReactComponent as ArrowDownIcon } from '@assets/images/Icons/Arrow_2_down.svg'
import { ReactComponent as ArrowCircleRightIcon } from '@assets/images/Icons/Arrow_circle_right.svg'
import { ReactComponent as CalendarIcon } from '@assets/images/Icons/Calendar.svg'
import { ReactComponent as InfoIcon } from '@assets/images/Icons/Info.svg'
import { ReactComponent as RecurringIcon } from '@assets/images/ScheduleCard/icon-recurring.svg'
import { ReactComponent as StatusIcon } from '@assets/images/ScheduleCard/icon-status.svg'
import { ReactComponent as InsuranceIcon } from '@assets/images/ScheduleCard/icon-insurance.svg'
import Button from '../Button/Button.component'
import Calendar from '../Calendar/Calendar.component'
import { WEEKDAY_SHORT_LABELS } from '../Calendar/Calendar.utils'
import Checkbox from '../Checkbox/Checkbox.component'
import Modal from '../Modal/Modal.component'
import Search from '../Search/Search.component'
import SearchWithFilter from '../Search/SearchWithFilter.component'
import type {
  FilterSelection,
  SearchFilterCustomRenderer,
} from '../Search/SearchWithFilter.types'
import ScheduleCard from '../ScheduleCard/ScheduleCard.component'
import { getScheduleCardColorTokens } from '../ScheduleCard/ScheduleCard.types'
import Tabs from '../Tabs/Tabs.component'
import SchedulerAppointmentInfoPanel from './SchedulerAppointmentInfo.component'
import SchedulerContextMenu, {
  type SchedulerContextMenuAction,
} from './SchedulerContextMenu.component'
import type {
  SchedulerApptStatus,
  SchedulerBlockRange,
  SchedulerColumn,
  SchedulerEvent,
  SchedulerProps,
  SchedulerView,
  SchedulerViewMode,
} from './Scheduler.types'
import {
  SCHEDULER_DEMO_BLOCK_RANGES,
  SCHEDULER_DEMO_COLUMNS,
  SCHEDULER_DEMO_DATE,
  SCHEDULER_DEMO_HIGHLIGHT_BANDS,
  SCHEDULER_DEMO_PROVIDERS,
  SCHEDULER_VIEW_ITEMS,
  SCHEDULER_APPT_STATUSES,
  createDefaultSchedulerFilters,
  createSchedulerDemoEvents,
} from './Scheduler.types'
import {
  applySchedulerFilters,
  eventOverlapsBlock,
  isSchedulerTimeInPast,
  filterEventsForDay,
  filterEventsForRange,
  formatEventTimeRange,
  formatHourLabel,
  formatSchedulerDateLabel,
  formatWeekDayColumnLabel,
  getEventLayout,
  getSchedulerColumnHit,
  layoutOverlappingEvents,
  getMonthGridDays,
  getWeekDays,
  groupEventsByDayAndProvider,
  groupEventsByDayKey,
  isSameDay,
  type SchedulerProviderGroup,
  minutesToDateOnDay,
  shiftSchedulerDate,
  snapMinutes,
  startOfDay,
  startOfMonth,
  toDayKey,
  updateEventInfoDateTime,
  withUpdatedEventStatus,
} from './Scheduler.utils'
import './Scheduler.css'

const DEFAULT_HEADER_ICONS = (
  <>
    <RecurringIcon aria-hidden="true" />
    <StatusIcon aria-hidden="true" />
    <InsuranceIcon aria-hidden="true" />
  </>
)

const MONTH_PROVIDER_VISIBLE = 3
const SNAP_MINUTES = 15

const formatMoveMeta = (date: Date): string => {
  const day = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${day} - ${time}`
}

type PendingMove = {
  event: SchedulerEvent
  nextColumnId: string
  nextStart: Date
  nextEnd: Date
  sourceColumnLabel: string
  targetColumnLabel: string
}

type ContextMenuState = {
  event: SchedulerEvent
  top: number
  left: number
}

type DragSession = {
  mode: 'move' | 'resize'
  eventId: string
  columnId: string
  startX: number
  startY: number
  originTop: number
  originLeft: number
  originHeight: number
  originWidth: number
  grabOffsetY: number
  durationMs: number
}

type DragPreviewState = {
  eventId: string
  top: number
  left: number
  width: number
  height: number
  columnId: string
}

const INFO_PANEL_WIDTH = 260
const INFO_PANEL_GAP = 12
const INFO_PANEL_EST_HEIGHT = 420

const Scheduler: React.FC<SchedulerProps> = ({
  columns = SCHEDULER_DEMO_COLUMNS,
  events: eventsProp,
  date: dateProp,
  defaultDate = SCHEDULER_DEMO_DATE,
  view: viewProp,
  defaultView = 'day',
  viewMode: viewModeProp,
  defaultViewMode = 'Fixed',
  dayStartHour = 8,
  dayEndHour = 21,
  pixelsPerHour: pixelsPerHourProp = 64,
  highlightBands = SCHEDULER_DEMO_HIGHLIGHT_BANDS,
  blockRanges = SCHEDULER_DEMO_BLOCK_RANGES,
  providers = SCHEDULER_DEMO_PROVIDERS,
  filters: filtersProp,
  defaultFilters,
  searchValue: searchProp,
  searchPlaceholder = 'Search appointment',
  showToolbar = true,
  enableInteractions = true,
  showEventDetailModal = true,
  darkMode = false,
  className = '',
  style,
  onDateChange,
  onViewChange,
  onSearchChange,
  onFiltersChange,
  onEventsChange,
  onTodayClick,
  onAddPatientClick,
  onSetAppointmentClick,
  onEventClick,
  onSlotClick,
  onInfoClick,
}) => {
  const [internalDate, setInternalDate] = useState(() =>
    startOfDay(dateProp ?? defaultDate),
  )
  const [internalView, setInternalView] = useState<SchedulerView>(
    viewProp ?? defaultView,
  )
  const [internalViewMode, setInternalViewMode] = useState<SchedulerViewMode>(
    viewModeProp ?? defaultViewMode,
  )
  const [internalSearch, setInternalSearch] = useState(searchProp ?? '')
  const [internalFilters, setInternalFilters] = useState(
    () => defaultFilters ?? createDefaultSchedulerFilters(columns, providers),
  )
  const [internalEvents, setInternalEvents] = useState(() =>
    createSchedulerDemoEvents(dateProp ?? defaultDate),
  )
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [infoAnchor, setInfoAnchor] = useState<{
    top: number
    left: number
  } | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [moveProviderId, setMoveProviderId] = useState('')
  const [moveSearch, setMoveSearch] = useState('')
  const [providerMenu, setProviderMenu] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const [unavailableOpen, setUnavailableOpen] = useState(false)
  const [unavailableReason, setUnavailableReason] = useState<
    'blocked' | 'past' | 'occupied'
  >('blocked')
  const [detailEvent, setDetailEvent] = useState<SchedulerEvent | null>(null)
  const [cancelEvent, setCancelEvent] = useState<SchedulerEvent | null>(null)
  const [mockNotice, setMockNotice] = useState<string | null>(null)
  const [setAppointmentSlot, setSetAppointmentSlot] = useState<{
    start: Date
    end: Date
    columnId: string
  } | null>(null)
  const [setAppointmentOpen, setSetAppointmentOpen] = useState(false)
  const [monthMoreOpen, setMonthMoreOpen] = useState<{
    dayKey: string
    top: number
    left: number
    providers: SchedulerProviderGroup[]
  } | null>(null)
  const [fullPixelsPerHour, setFullPixelsPerHour] = useState(pixelsPerHourProp)
  const [dragPreview, setDragPreview] = useState<DragPreviewState | null>(null)
  const [hoverSlot, setHoverSlot] = useState<{
    columnId: string
    top: number
    height: number
  } | null>(null)

  const gridRef = useRef<HTMLDivElement>(null)
  const boardRef = useRef<HTMLDivElement>(null)
  const datePickerRef = useRef<HTMLDivElement>(null)
  const eventHostRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const dragSessionRef = useRef<DragSession | null>(null)
  const dragMovedRef = useRef(false)
  const suppressClickRef = useRef(false)
  const boardColumnsRef = useRef<SchedulerColumn[]>([])
  const infoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const date = dateProp ? startOfDay(dateProp) : internalDate
  const view = viewProp ?? internalView
  const viewMode = viewModeProp ?? internalViewMode
  const searchValue = searchProp ?? internalSearch
  const filters = filtersProp ?? internalFilters
  const events = eventsProp ?? internalEvents
  const pixelsPerHour =
    viewMode === 'Full' ? fullPixelsPerHour : pixelsPerHourProp

  const commitEvents = useCallback(
    (next: SchedulerEvent[]): void => {
      if (eventsProp === undefined) setInternalEvents(next)
      onEventsChange?.(next)
    },
    [eventsProp, onEventsChange],
  )

  const patchEvent = useCallback(
    (eventId: string, updater: (event: SchedulerEvent) => SchedulerEvent) => {
      commitEvents(
        events.map((event) => (event.id === eventId ? updater(event) : event)),
      )
    },
    [commitEvents, events],
  )

  useEffect(() => {
    if (dateProp) setInternalDate(startOfDay(dateProp))
  }, [dateProp])

  useEffect(() => {
    if (viewProp) setInternalView(viewProp)
  }, [viewProp])

  useEffect(() => {
    if (viewModeProp) setInternalViewMode(viewModeProp)
  }, [viewModeProp])

  useEffect(() => {
    if (searchProp !== undefined) setInternalSearch(searchProp)
  }, [searchProp])

  useEffect(() => {
    if (filtersProp) setInternalFilters(filtersProp)
  }, [filtersProp])

  useEffect(() => {
    if (!datePickerOpen) return undefined

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (datePickerOpen && !datePickerRef.current?.contains(target)) {
        setDatePickerOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setDatePickerOpen(false)
        setContextMenu(null)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [datePickerOpen])

  useEffect(() => {
    if (viewMode !== 'Full' || !boardRef.current) return undefined

    const node = boardRef.current
    const update = (): void => {
      const hourCount = Math.max(dayEndHour - dayStartHour, 1)
      const available = Math.max(node.clientHeight - 48, hourCount * 32)
      setFullPixelsPerHour(available / hourCount)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [viewMode, dayStartHour, dayEndHour, view])

  const hourCount = Math.max(dayEndHour - dayStartHour, 1)
  const gridHeight = hourCount * pixelsPerHour
  const hours = useMemo(
    () => Array.from({ length: hourCount }, (_, index) => dayStartHour + index),
    [dayStartHour, hourCount],
  )

  const weekDays = useMemo(() => getWeekDays(date), [date])
  const monthDays = useMemo(() => getMonthGridDays(date), [date])
  const monthStart = useMemo(() => startOfMonth(date), [date])

  const weekColumns = useMemo<SchedulerColumn[]>(
    () =>
      weekDays.map((day) => ({
        id: toDayKey(day),
        label: formatWeekDayColumnLabel(day),
      })),
    [weekDays],
  )

  const filteredEvents = useMemo(
    () => applySchedulerFilters(events, filters, searchValue),
    [events, filters, searchValue],
  )

  const dayColumns = useMemo(
    () =>
      filters.roomIds.length === 0
        ? columns
        : columns.filter((column) => filters.roomIds.includes(column.id)),
    [columns, filters.roomIds],
  )

  const searchFilterOptions = useMemo(
    () => ({
      providers: providers.map((provider) => provider.id),
      rooms: columns.map((column) => column.id),
      status: [...SCHEDULER_APPT_STATUSES],
    }),
    [columns, providers],
  )

  const searchFilterLabels = useMemo(
    () => ({
      providers: 'Providers',
      rooms: 'Rooms',
      status: 'Status',
    }),
    [],
  )

  const selectedSearchFilters = useMemo(
    (): FilterSelection => ({
      ...(filters.providerIds.length > 0
        ? { providers: filters.providerIds }
        : {}),
      ...(filters.roomIds.length > 0 ? { rooms: filters.roomIds } : {}),
      ...(filters.statuses.length > 0 ? { status: [...filters.statuses] } : {}),
    }),
    [filters],
  )

  const searchFilterRenderers = useMemo(() => {
    const renderers: Record<string, SearchFilterCustomRenderer> = {}

    providers.forEach((provider) => {
      renderers[`providers:${provider.id}`] = (
        _option,
        isChecked,
        onChange,
      ) => (
        <Checkbox
          label={provider.name}
          checked={isChecked}
          darkMode={darkMode}
          onChange={onChange}
        />
      )
    })

    columns.forEach((column) => {
      renderers[`rooms:${column.id}`] = (_option, isChecked, onChange) => (
        <Checkbox
          label={column.label}
          checked={isChecked}
          darkMode={darkMode}
          onChange={onChange}
        />
      )
    })

    return renderers
  }, [columns, darkMode, providers])

  const dayEvents = useMemo(
    () => filterEventsForDay(filteredEvents, date),
    [filteredEvents, date],
  )

  const weekEvents = useMemo(
    () => filterEventsForRange(filteredEvents, weekDays[0], weekDays[6]),
    [filteredEvents, weekDays],
  )

  const weekEventsByDay = useMemo(
    () => groupEventsByDayKey(weekEvents),
    [weekEvents],
  )

  const monthEvents = useMemo(
    () =>
      filterEventsForRange(
        filteredEvents,
        monthDays[0],
        monthDays[monthDays.length - 1],
      ),
    [filteredEvents, monthDays],
  )

  const monthSummary = useMemo(
    () => groupEventsByDayAndProvider(monthEvents),
    [monthEvents],
  )

  const boardColumns = view === 'day' ? dayColumns : weekColumns
  boardColumnsRef.current = boardColumns
  /** Match Appointment WeekCalendar Fixed columns (240px) so cards stay readable. */
  const weekColumnWidth = 240
  const columnMinWidth =
    view === 'day' ? 160 : view === 'week' ? weekColumnWidth : 120

  const eventLaneLayoutsByColumn = useMemo(() => {
    const layouts = new Map<
      string,
      ReturnType<typeof layoutOverlappingEvents>
    >()
    for (const column of boardColumns) {
      const columnEvents =
        view === 'day'
          ? dayEvents.filter((event) => event.columnId === column.id)
          : (weekEventsByDay[column.id] ?? [])
      layouts.set(column.id, layoutOverlappingEvents(columnEvents))
    }
    return layouts
  }, [boardColumns, dayEvents, view, weekEventsByDay])

  const draggingEvent = useMemo(
    () =>
      dragPreview
        ? (events.find((event) => event.id === dragPreview.eventId) ?? null)
        : null,
    [dragPreview, events],
  )

  const selectedEvent = useMemo(
    () =>
      [...dayEvents, ...weekEvents, ...monthEvents].find(
        (event) => event.id === selectedEventId,
      ) ?? null,
    [dayEvents, weekEvents, monthEvents, selectedEventId],
  )

  const setDate = (next: Date): void => {
    const normalized = startOfDay(next)
    if (!dateProp) setInternalDate(normalized)
    onDateChange?.(normalized)
  }

  const setView = (next: SchedulerView): void => {
    if (!viewProp) setInternalView(next)
    onViewChange?.(next)
    setSelectedEventId(null)
    setInfoAnchor(null)
    setContextMenu(null)
  }

  const setSearch = (next: string): void => {
    if (searchProp === undefined) setInternalSearch(next)
    onSearchChange?.(next)
  }

  const setFilters = (next: typeof filters): void => {
    if (!filtersProp) setInternalFilters(next)
    onFiltersChange?.(next)
  }

  const handleSearchFilterChange = (next: FilterSelection): void => {
    setFilters({
      providerIds: next.providers ?? [],
      roomIds: next.rooms ?? [],
      statuses: (next.status ?? []) as SchedulerApptStatus[],
    })
  }

  const handleToday = (): void => {
    setDate(new Date())
    onTodayClick?.()
  }

  const handleNav = (direction: -1 | 1): void => {
    setDate(shiftSchedulerDate(date, view, direction))
  }

  const handleMonthDayClick = (day: Date): void => {
    setMonthMoreOpen(null)
    setDate(day)
    setView('day')
  }

  const openMonthMore = (
    dayKey: string,
    overflowProviders: SchedulerProviderGroup[],
    anchor: HTMLElement,
  ): void => {
    const rect = anchor.getBoundingClientRect()
    const width = 220
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    const top = Math.min(rect.bottom + 4, window.innerHeight - 160)
    setMonthMoreOpen({
      dayKey,
      top,
      left,
      providers: overflowProviders,
    })
  }

  const clearInfoCloseTimeout = (): void => {
    if (infoCloseTimeoutRef.current) {
      clearTimeout(infoCloseTimeoutRef.current)
      infoCloseTimeoutRef.current = null
    }
  }

  const openInfoForEvent = (event: SchedulerEvent): void => {
    if (!event.info) return
    clearInfoCloseTimeout()
    const host = eventHostRefs.current[event.id]
    setSelectedEventId(event.id)
    if (!host) return

    const hostRect = host.getBoundingClientRect()
    const spaceRight = window.innerWidth - hostRect.right - INFO_PANEL_GAP
    const placeOnLeft = spaceRight < INFO_PANEL_WIDTH

    const rawLeft = placeOnLeft
      ? hostRect.left - INFO_PANEL_WIDTH - INFO_PANEL_GAP
      : hostRect.right + INFO_PANEL_GAP
    const left = Math.max(
      8,
      Math.min(rawLeft, window.innerWidth - INFO_PANEL_WIDTH - 8),
    )
    const top = Math.max(
      8,
      Math.min(hostRect.top, window.innerHeight - INFO_PANEL_EST_HEIGHT - 8),
    )

    setInfoAnchor({ top, left })
  }

  const handleCloseInfo = (): void => {
    clearInfoCloseTimeout()
    setSelectedEventId(null)
    setInfoAnchor(null)
  }

  const scheduleCloseInfo = (): void => {
    clearInfoCloseTimeout()
    infoCloseTimeoutRef.current = setTimeout(() => {
      setSelectedEventId(null)
      setInfoAnchor(null)
      infoCloseTimeoutRef.current = null
    }, 120)
  }

  const handleEventMouseEnter = (event: SchedulerEvent): void => {
    if (dragSessionRef.current || contextMenu) return
    openInfoForEvent(event)
  }

  const handleEventClick = (event: SchedulerEvent): void => {
    onEventClick?.(event)
    if (enableInteractions && showEventDetailModal) {
      setDetailEvent(event)
    }
  }

  const openSetAppointment = (slot?: {
    start: Date
    end: Date
    columnId: string
  }): void => {
    if (slot && onSlotClick) {
      onSlotClick(slot)
      return
    }
    if (!slot && onSetAppointmentClick) {
      onSetAppointmentClick()
      return
    }
    // Showcase / no host modal: open built-in confirm sheet
    if (slot) {
      setSetAppointmentSlot(slot)
    } else {
      const start = minutesToDateOnDay(date, dayStartHour * 60 + 60)
      const end = minutesToDateOnDay(date, dayStartHour * 60 + 90)
      setSetAppointmentSlot({
        start,
        end,
        columnId: boardColumns[0]?.id ?? columns[0]?.id ?? '',
      })
    }
    setSetAppointmentOpen(true)
  }

  const handleSetAppointmentButtonClick = (): void => {
    openSetAppointment()
  }

  const handleGridSlotClick = (domEvent: React.MouseEvent): void => {
    if (!enableInteractions) return
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    const target = domEvent.target as HTMLElement
    if (
      target.closest('.ds-scheduler__event') ||
      target.closest('.ds-scheduler__resize-handle')
    ) {
      return
    }

    const grid = gridRef.current
    if (!grid) return
    const rect = grid.getBoundingClientRect()
    const y = domEvent.clientY - rect.top
    const hit = getSchedulerColumnHit(grid, domEvent.clientX)
    if (!hit) return
    const column = boardColumns[hit.index]
    if (!column) return

    const rawMinutes = dayStartHour * 60 + (y / pixelsPerHour) * 60
    const startMinutes = Math.min(
      Math.max(snapMinutes(rawMinutes, SNAP_MINUTES), dayStartHour * 60),
      dayEndHour * 60 - SNAP_MINUTES,
    )
    const endMinutes = Math.min(startMinutes + 30, dayEndHour * 60)

    const dayForSlot =
      view === 'week'
        ? (weekDays.find((day) => toDayKey(day) === column.id) ?? date)
        : date

    openSetAppointment({
      start: minutesToDateOnDay(dayForSlot, startMinutes),
      end: minutesToDateOnDay(dayForSlot, endMinutes),
      columnId: column.id,
    })
  }

  const handleGridSlotHover = (domEvent: React.MouseEvent): void => {
    if (!enableInteractions || dragSessionRef.current) {
      setHoverSlot(null)
      return
    }

    const target = domEvent.target as HTMLElement
    if (
      target.closest('.ds-scheduler__event') ||
      target.closest('.ds-scheduler__resize-handle')
    ) {
      setHoverSlot(null)
      return
    }

    const grid = gridRef.current
    if (!grid) return
    const rect = grid.getBoundingClientRect()
    const y = domEvent.clientY - rect.top
    const hit = getSchedulerColumnHit(grid, domEvent.clientX)
    if (!hit) {
      setHoverSlot(null)
      return
    }
    const column = boardColumns[hit.index]
    if (!column) {
      setHoverSlot(null)
      return
    }

    const rawMinutes = dayStartHour * 60 + (y / pixelsPerHour) * 60
    const startMinutes = Math.min(
      Math.max(snapMinutes(rawMinutes, SNAP_MINUTES), dayStartHour * 60),
      dayEndHour * 60 - SNAP_MINUTES,
    )
    const height = (SNAP_MINUTES / 60) * pixelsPerHour
    const top = ((startMinutes - dayStartHour * 60) / 60) * pixelsPerHour

    setHoverSlot((prev) => {
      if (
        prev &&
        prev.columnId === column.id &&
        prev.top === top &&
        prev.height === height
      ) {
        return prev
      }
      return { columnId: column.id, top, height }
    })
  }

  const resolveDrop = (
    clientX: number,
    clientY: number,
    session: DragSession,
  ): PendingMove | 'blocked' | 'past' | 'occupied' | null => {
    const grid = gridRef.current
    if (!grid) return null
    const rect = grid.getBoundingClientRect()
    const y = clientY - rect.top
    const hit = getSchedulerColumnHit(grid, clientX)
    if (!hit) return null
    const column = boardColumns[hit.index]
    if (!column) return null

    const event = events.find((item) => item.id === session.eventId)
    if (!event) return null

    let nextStart: Date
    let nextEnd: Date
    let nextColumnId = column.id

    if (session.mode === 'resize') {
      const endMinutes = snapMinutes(
        dayStartHour * 60 + (y / pixelsPerHour) * 60,
        SNAP_MINUTES,
      )
      const startMinutes =
        event.start.getHours() * 60 + event.start.getMinutes()
      const clampedEnd = Math.max(endMinutes, startMinutes + SNAP_MINUTES)
      nextStart = event.start
      nextEnd = minutesToDateOnDay(event.start, clampedEnd)
      nextColumnId = event.columnId
    } else {
      const minutes = snapMinutes(
        dayStartHour * 60 + (Math.max(y, 0) / pixelsPerHour) * 60,
        SNAP_MINUTES,
      )
      const dayForColumn =
        view === 'week'
          ? (weekDays.find((day) => toDayKey(day) === column.id) ?? date)
          : date
      nextStart = minutesToDateOnDay(dayForColumn, minutes)
      nextEnd = new Date(nextStart.getTime() + session.durationMs)
      nextColumnId = view === 'week' ? event.columnId : column.id
      // Week: column is day key — keep room, change day; Day: column is room
      if (view === 'week') {
        nextColumnId = event.columnId
      }
    }

    if (session.mode === 'move' && isSchedulerTimeInPast(nextStart)) {
      return 'past'
    }

    const columnForBlock = view === 'week' ? event.columnId : nextColumnId
    if (eventOverlapsBlock(nextStart, nextEnd, columnForBlock, blockRanges)) {
      return 'blocked'
    }

    const roomIsOccupied = events.some(
      (other) =>
        other.id !== event.id &&
        other.columnId === nextColumnId &&
        other.start < nextEnd &&
        other.end > nextStart,
    )
    if (roomIsOccupied) return 'occupied'

    const sourceLabel =
      columns.find((room) => room.id === event.columnId)?.label ??
      event.columnId
    const targetLabel =
      view === 'day'
        ? (columns.find((room) => room.id === nextColumnId)?.label ??
          nextColumnId)
        : formatWeekDayColumnLabel(nextStart)

    return {
      event,
      nextColumnId: view === 'week' ? event.columnId : nextColumnId,
      nextStart,
      nextEnd,
      sourceColumnLabel: sourceLabel,
      targetColumnLabel: targetLabel,
    }
  }

  const handleMouseMove = useCallback(
    (event: MouseEvent): void => {
      const session = dragSessionRef.current
      const grid = gridRef.current
      if (!session || !grid) return

      const movedFarEnough =
        Math.abs(event.clientY - session.startY) >= 4 ||
        Math.abs(event.clientX - session.startX) >= 4
      if (!dragMovedRef.current && !movedFarEnough) {
        return
      }
      dragMovedRef.current = true

      const rect = grid.getBoundingClientRect()
      const y = event.clientY - rect.top
      const activeColumns = boardColumnsRef.current
      const hit = getSchedulerColumnHit(grid, event.clientX)
      if (!hit) return

      if (session.mode === 'resize') {
        const height = Math.max(y - session.originTop, pixelsPerHour / 4)
        setDragPreview({
          eventId: session.eventId,
          top: session.originTop,
          left: session.originLeft,
          width: session.originWidth,
          height,
          columnId: session.columnId,
        })
        return
      }

      const targetColumn = activeColumns[hit.index]
      const left = hit.left + 4
      const width = Math.max(hit.width - 8, 24)
      const top = Math.min(
        Math.max(y - session.grabOffsetY, 0),
        Math.max(rect.height - session.originHeight, 0),
      )

      setDragPreview({
        eventId: session.eventId,
        top,
        left,
        width,
        height: session.originHeight,
        columnId: targetColumn?.id ?? session.columnId,
      })
    },
    [pixelsPerHour],
  )

  const handleMouseUp = useCallback(
    (event: MouseEvent): void => {
      const session = dragSessionRef.current
      const didMove = dragMovedRef.current
      dragSessionRef.current = null
      dragMovedRef.current = false
      if (didMove) suppressClickRef.current = true
      setDragPreview(null)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (!session || !didMove) return

      const result = resolveDrop(event.clientX, event.clientY, session)
      if (result === 'blocked' || result === 'past' || result === 'occupied') {
        setUnavailableReason(result)
        setUnavailableOpen(true)
        return
      }
      if (!result) return

      if (session.mode === 'resize') {
        patchEvent(result.event.id, (current) =>
          updateEventInfoDateTime({
            ...current,
            end: result.nextEnd,
          }),
        )
        return
      }

      setMoveProviderId(result.event.providerId ?? '')
      setMoveSearch('')
      setProviderMenu(null)
      setPendingMove(result)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleMouseMove,
      patchEvent,
      pixelsPerHour,
      boardColumns,
      view,
      date,
      weekDays,
      blockRanges,
      columns,
      events,
    ],
  )

  const beginDrag = (
    event: SchedulerEvent,
    mode: 'move' | 'resize',
    mouseEvent: React.MouseEvent,
  ): void => {
    if (!enableInteractions || view === 'month') return
    if (mode === 'resize') {
      mouseEvent.preventDefault()
      mouseEvent.stopPropagation()
    }
    const host = eventHostRefs.current[event.id]
    const grid = gridRef.current
    if (!host || !grid) return
    const gridRect = grid.getBoundingClientRect()
    const hostRect = host.getBoundingClientRect()
    const layout = getEventLayout(event, dayStartHour, pixelsPerHour)
    const originTop = hostRect.top - gridRect.top
    const originLeft = hostRect.left - gridRect.left
    const originWidth = hostRect.width
    dragMovedRef.current = mode === 'resize'
    dragSessionRef.current = {
      mode,
      eventId: event.id,
      columnId: event.columnId,
      startX: mouseEvent.clientX,
      startY: mouseEvent.clientY,
      originTop,
      originLeft,
      originHeight: layout.height,
      originWidth,
      grabOffsetY: mouseEvent.clientY - hostRect.top,
      durationMs: event.end.getTime() - event.start.getTime(),
    }
    setDragPreview({
      eventId: event.id,
      top: originTop,
      left: originLeft,
      width: originWidth,
      height: layout.height,
      columnId: event.columnId,
    })
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const applyPendingMove = (providerId?: string): void => {
    if (!pendingMove) return
    const provider = providers.find((item) => item.id === providerId)
    patchEvent(pendingMove.event.id, (current) =>
      updateEventInfoDateTime({
        ...current,
        columnId: pendingMove.nextColumnId,
        start: pendingMove.nextStart,
        end: pendingMove.nextEnd,
        providerId: providerId || current.providerId,
        providerName: provider?.name ?? current.providerName,
        info: current.info
          ? {
              ...current.info,
              provider: provider?.name ?? current.info.provider,
            }
          : current.info,
      }),
    )
    setPendingMove(null)
    setProviderMenu(null)
    setMoveSearch('')
  }

  const closeMoveModal = (): void => {
    setPendingMove(null)
    setProviderMenu(null)
    setMoveSearch('')
  }

  const filteredMoveProviders = useMemo(() => {
    const query = moveSearch.trim().toLowerCase()
    if (!query) return providers
    return providers.filter((provider) =>
      provider.name.toLowerCase().includes(query),
    )
  }, [moveSearch, providers])

  const handleContextMenu = (
    event: SchedulerEvent,
    domEvent: React.MouseEvent,
  ): void => {
    if (!enableInteractions) return
    domEvent.preventDefault()
    handleCloseInfo()
    setContextMenu({
      event,
      top: domEvent.clientY,
      left: domEvent.clientX,
    })
  }

  useEffect(
    () => () => {
      clearInfoCloseTimeout()
    },
    [],
  )

  const setEventStatus = (
    event: SchedulerEvent,
    status: SchedulerApptStatus,
  ): void => {
    patchEvent(event.id, (current) => withUpdatedEventStatus(current, status))
    setContextMenu(null)
  }

  const handleContextAction = (
    event: SchedulerEvent,
    action: SchedulerContextMenuAction,
  ): void => {
    switch (action.type) {
      case 'status':
        setEventStatus(event, action.status)
        return
      case 'info':
        if (action.target === 'appt') {
          if (showEventDetailModal) {
            setDetailEvent(event)
          } else {
            onEventClick?.(event)
          }
          return
        }
        setMockNotice(
          `Patient info for ${event.patientName} (mock — not wired to patient profile).`,
        )
        return
      case 'cancel':
        setCancelEvent(event)
        return
      case 'goto': {
        const labels: Record<string, string> = {
          treatmentplan: 'Treatment plan',
          periocharting: 'Perio charting',
          xrayimaging: 'X-ray imaging',
          laborder: 'Lab order',
          checkcoverage: 'Check coverage',
          reschedule: 'Reschedule',
          rqpreauth: 'Rq pre-auth',
          rqclaim: 'Rq claim',
        }
        setMockNotice(
          `Go to "${labels[action.target] ?? action.target}" (mock — not wired).`,
        )
        return
      }
      case 'contact':
        setMockNotice(
          `${action.target[0].toUpperCase()}${action.target.slice(1)} ${event.patientName} (mock — not wired).`,
        )
        return
      case 'referto':
        setMockNotice('Refer to (mock — not wired).')
        return
      case 'notify':
        setMockNotice(`Notify ${event.patientName} (mock — not wired).`)
        return
      case 'addnote':
        setMockNotice(`Add note for ${event.patientName} (mock — not wired).`)
        return
      default:
        return
    }
  }

  const gridTemplateColumns =
    view === 'week'
      ? `repeat(${boardColumns.length}, minmax(${weekColumnWidth}px, 1fr))`
      : `repeat(${boardColumns.length}, minmax(${columnMinWidth}px, 1fr))`
  const classNames = [
    'ds-scheduler',
    viewMode === 'Full' ? 'ds-scheduler--full' : '',
    darkMode ? 'ds-scheduler--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  const navUnitLabel =
    view === 'week' ? 'week' : view === 'month' ? 'month' : 'day'

  const eventsForColumn = (columnId: string): SchedulerEvent[] => {
    if (view === 'day') {
      return dayEvents.filter((event) => event.columnId === columnId)
    }
    return weekEventsByDay[columnId] ?? []
  }

  const globalBlocks = useMemo(
    () => blockRanges.filter((block) => !block.columnId),
    [blockRanges],
  )

  const blocksForColumn = (columnId: string): SchedulerBlockRange[] =>
    blockRanges.filter((block) => block.columnId === columnId)

  return (
    <div className={classNames} style={style}>
      {showToolbar ? (
        <header className="ds-scheduler__toolbar">
          <div className="ds-scheduler__toolbar-left">
            <Button
              variant="secondary"
              size="md"
              label="Today"
              darkMode={darkMode}
              onClick={handleToday}
            />
            <Tabs
              items={SCHEDULER_VIEW_ITEMS}
              value={view}
              darkMode={darkMode}
              onChange={(next) => setView(next as SchedulerView)}
            />
            <span className="ds-scheduler__divider" aria-hidden="true" />
            <div
              className={`ds-scheduler__date-picker${
                datePickerOpen ? ' ds-scheduler__date-picker--open' : ''
              }`}
              ref={datePickerRef}
            >
              <button
                type="button"
                className="ds-scheduler__date-trigger"
                aria-haspopup="dialog"
                aria-expanded={datePickerOpen}
                aria-label="Select date"
                onClick={() => setDatePickerOpen((open) => !open)}
              >
                <CalendarIcon aria-hidden="true" />
                <span className="ds-scheduler__date-text">
                  {formatSchedulerDateLabel(date, view)}
                </span>
                <ArrowDownIcon
                  className="ds-scheduler__date-chevron"
                  aria-hidden="true"
                />
              </button>
              {datePickerOpen ? (
                <div className="ds-scheduler__date-popover" role="dialog">
                  <Calendar
                    mode="date"
                    value={date}
                    defaultViewDate={date}
                    darkMode={darkMode}
                    onChange={(next) => {
                      setDate(next)
                      setDatePickerOpen(false)
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="ds-scheduler__date-nav" role="group">
              <button
                type="button"
                className="ds-scheduler__nav-btn"
                aria-label={`Previous ${navUnitLabel}`}
                onClick={() => handleNav(-1)}
              >
                <ArrowLeftIcon aria-hidden="true" />
              </button>
              <button
                type="button"
                className="ds-scheduler__nav-btn"
                aria-label={`Next ${navUnitLabel}`}
                onClick={() => handleNav(1)}
              >
                <ArrowRightIcon aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="ds-scheduler__toolbar-right">
            <button
              type="button"
              className="ds-scheduler__info-btn"
              aria-label="Scheduler information"
              onClick={onInfoClick}
            >
              <InfoIcon aria-hidden="true" />
            </button>
            <div className="ds-scheduler__search">
              <SearchWithFilter
                className="ds-scheduler__search-control"
                value={searchValue}
                placeholder={searchPlaceholder}
                darkMode={darkMode}
                filters={searchFilterOptions}
                filterLabels={searchFilterLabels}
                selectedFilters={selectedSearchFilters}
                customRenderers={searchFilterRenderers}
                showResetButton
                onFilterChange={handleSearchFilterChange}
                onReset={() =>
                  setFilters(createDefaultSchedulerFilters(columns, providers))
                }
                onChange={setSearch}
                onClear={() => setSearch('')}
              />
            </div>
            <span className="ds-scheduler__divider" aria-hidden="true" />
            <Button
              variant="secondary"
              size="md"
              label="Add patient"
              darkMode={darkMode}
              onClick={onAddPatientClick}
            />
            <Button
              variant="primary"
              size="md"
              label="Set appointment"
              darkMode={darkMode}
              onClick={handleSetAppointmentButtonClick}
            />
          </div>
        </header>
      ) : null}

      {view === 'day' || view === 'week' ? (
        <div
          className={`ds-scheduler__board${
            viewMode === 'Full' ? ' ds-scheduler__board--full' : ''
          }${view === 'week' ? ' ds-scheduler__board--week' : ''}`}
          ref={boardRef}
        >
          <div className="ds-scheduler__time-col">
            <div className="ds-scheduler__time-corner" aria-hidden="true" />
            <div
              className="ds-scheduler__time-rail"
              style={{ height: gridHeight }}
            >
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="ds-scheduler__time-label"
                  style={{ height: pixelsPerHour }}
                >
                  {formatHourLabel(hour)}
                </div>
              ))}
            </div>
          </div>

          <div className="ds-scheduler__table">
            <div
              className="ds-scheduler__columns-header"
              style={{ gridTemplateColumns }}
            >
              {boardColumns.map((column) => {
                const dayCount =
                  view === 'week'
                    ? (weekEventsByDay[column.id]?.length ?? 0)
                    : null
                return (
                  <div
                    key={column.id}
                    className={`ds-scheduler__column-header${
                      view === 'week'
                        ? ' ds-scheduler__column-header--week'
                        : ''
                    }`}
                  >
                    <span className="ds-scheduler__column-header-label">
                      {column.label}
                    </span>
                    {dayCount !== null ? (
                      <span
                        className="ds-scheduler__column-header-count"
                        aria-label={`${dayCount} appointments`}
                      >
                        <i
                          className="mgc_group_3_fill ds-scheduler__column-header-count-icon"
                          aria-hidden="true"
                        />
                        <span>{dayCount}</span>
                      </span>
                    ) : null}
                  </div>
                )
              })}
            </div>

            <div
              className="ds-scheduler__grid"
              ref={gridRef}
              style={{ height: gridHeight, gridTemplateColumns }}
              onClick={handleGridSlotClick}
              onMouseMove={handleGridSlotHover}
              onMouseLeave={() => setHoverSlot(null)}
            >
              {highlightBands.map((band) => {
                const top = (band.startHour - dayStartHour) * pixelsPerHour
                const height = (band.endHour - band.startHour) * pixelsPerHour
                const tone = band.tone ?? 'yellow'
                return (
                  <div
                    key={band.id}
                    className={`ds-scheduler__highlight ds-scheduler__highlight--${tone}`}
                    style={{ top, height }}
                    aria-hidden="true"
                  />
                )
              })}

              {globalBlocks.map((block) => {
                const top = (block.startHour - dayStartHour) * pixelsPerHour
                const height = (block.endHour - block.startHour) * pixelsPerHour
                return (
                  <div
                    key={`global-${block.id}`}
                    className="ds-scheduler__block ds-scheduler__block--span"
                    style={{ top, height }}
                    aria-hidden="true"
                  />
                )
              })}

              {hours.map((hour) => {
                const hourTop = (hour - dayStartHour) * pixelsPerHour
                const quarter = pixelsPerHour / 4
                return (
                  <React.Fragment key={`slots-${hour}`}>
                    <div
                      className="ds-scheduler__slot-line ds-scheduler__slot-line--dashed"
                      style={{ top: hourTop + quarter }}
                      aria-hidden="true"
                    />
                    <div
                      className="ds-scheduler__slot-line ds-scheduler__slot-line--solid"
                      style={{ top: hourTop + quarter * 2 }}
                      aria-hidden="true"
                    />
                    <div
                      className="ds-scheduler__slot-line ds-scheduler__slot-line--dashed"
                      style={{ top: hourTop + quarter * 3 }}
                      aria-hidden="true"
                    />
                    <div
                      className="ds-scheduler__slot-line ds-scheduler__slot-line--hour"
                      style={{ top: hourTop + pixelsPerHour }}
                      aria-hidden="true"
                    />
                  </React.Fragment>
                )
              })}

              {boardColumns.map((column) => (
                <div key={column.id} className="ds-scheduler__column">
                  {hoverSlot?.columnId === column.id ? (
                    <div
                      className="ds-scheduler__slot-hover"
                      style={{
                        top: hoverSlot.top,
                        height: hoverSlot.height,
                      }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {blocksForColumn(column.id).map((block) => {
                    const top = (block.startHour - dayStartHour) * pixelsPerHour
                    const height =
                      (block.endHour - block.startHour) * pixelsPerHour
                    return (
                      <div
                        key={`${column.id}-${block.id}`}
                        className="ds-scheduler__block"
                        style={{ top, height }}
                        aria-hidden="true"
                      />
                    )
                  })}

                  {eventsForColumn(column.id).map((event) => {
                    const layout = getEventLayout(
                      event,
                      dayStartHour,
                      pixelsPerHour,
                    )
                    const laneLayout = eventLaneLayoutsByColumn
                      .get(column.id)
                      ?.get(event.id)
                    const lane = laneLayout?.lane ?? 0
                    const laneCount = laneLayout?.laneCount ?? 1
                    const isSelected = event.id === selectedEventId
                    const isDragging =
                      dragPreview?.eventId === event.id &&
                      dragSessionRef.current?.mode === 'move'
                    return (
                      <div
                        key={event.id}
                        ref={(element) => {
                          eventHostRefs.current[event.id] = element
                        }}
                        className={`ds-scheduler__event${
                          isSelected ? ' ds-scheduler__event--selected' : ''
                        }${isDragging ? ' ds-scheduler__event--origin' : ''}`}
                        style={{
                          top: layout.top,
                          height:
                            dragPreview?.eventId === event.id &&
                            dragSessionRef.current?.mode === 'resize'
                              ? dragPreview.height
                              : layout.height,
                          left: `calc(${(lane / laneCount) * 100}% + 2px)`,
                          width: `calc(${100 / laneCount}% - 4px)`,
                          right: 'auto',
                        }}
                        onContextMenu={(domEvent) =>
                          handleContextMenu(event, domEvent)
                        }
                        onMouseEnter={() => handleEventMouseEnter(event)}
                        onMouseLeave={scheduleCloseInfo}
                        onClick={() => {
                          if (suppressClickRef.current) {
                            suppressClickRef.current = false
                            return
                          }
                          handleEventClick(event)
                        }}
                        onMouseDown={(domEvent) => {
                          if (
                            (domEvent.target as HTMLElement).closest(
                              '.ds-scheduler__resize-handle',
                            )
                          ) {
                            return
                          }
                          handleCloseInfo()
                          beginDrag(event, 'move', domEvent)
                        }}
                      >
                        <ScheduleCard
                          color={event.color}
                          timeRange={formatEventTimeRange(
                            event.start,
                            event.end,
                          )}
                          patientName={event.patientName}
                          appointmentType={event.appointmentType}
                          headerIcons={
                            event.headerIcons ?? DEFAULT_HEADER_ICONS
                          }
                          aiAssisted={event.aiAssisted}
                          aiHint={event.aiHint}
                        />
                        {enableInteractions ? (
                          <button
                            type="button"
                            className="ds-scheduler__resize-handle"
                            aria-label="Resize appointment"
                            onMouseDown={(domEvent) =>
                              beginDrag(event, 'resize', domEvent)
                            }
                          />
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              ))}

              {draggingEvent &&
              dragPreview &&
              dragSessionRef.current?.mode !== 'resize' ? (
                <div
                  className="ds-scheduler__event ds-scheduler__event--ghost"
                  style={{
                    top: dragPreview.top,
                    left: dragPreview.left,
                    width: dragPreview.width,
                    height: dragPreview.height,
                  }}
                >
                  <ScheduleCard
                    color={draggingEvent.color}
                    timeRange={formatEventTimeRange(
                      draggingEvent.start,
                      draggingEvent.end,
                    )}
                    patientName={draggingEvent.patientName}
                    appointmentType={draggingEvent.appointmentType}
                    headerIcons={
                      draggingEvent.headerIcons ?? DEFAULT_HEADER_ICONS
                    }
                    aiAssisted={draggingEvent.aiAssisted}
                    aiHint={draggingEvent.aiHint}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="ds-scheduler__board ds-scheduler__board--month"
          ref={boardRef}
        >
          <div className="ds-scheduler__table ds-scheduler__table--month">
            <div className="ds-scheduler__month-header">
              {WEEKDAY_SHORT_LABELS.map((label) => (
                <div key={label} className="ds-scheduler__month-weekday">
                  {label}
                </div>
              ))}
            </div>
            <div className="ds-scheduler__month-grid">
              {monthDays.map((day) => {
                const dayKey = toDayKey(day)
                const summary = monthSummary[dayKey]
                const providersForDay = summary?.providers ?? []
                const visibleProviders = providersForDay.slice(
                  0,
                  MONTH_PROVIDER_VISIBLE,
                )
                const remainingProviders = providersForDay.slice(
                  MONTH_PROVIDER_VISIBLE,
                )
                const overflowCount = remainingProviders.length
                const isCurrentMonth = day.getMonth() === monthStart.getMonth()
                const isToday = isSameDay(day, new Date())
                const moreOpen = monthMoreOpen?.dayKey === dayKey

                return (
                  <div
                    key={dayKey}
                    role="button"
                    tabIndex={0}
                    aria-label={`${day.toLocaleDateString(undefined, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}, ${summary?.totalAppointments ?? 0} appointments`}
                    className={[
                      'ds-scheduler__month-cell',
                      !isCurrentMonth ? 'ds-scheduler__month-cell--other' : '',
                      isToday ? 'ds-scheduler__month-cell--today' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleMonthDayClick(day)}
                    onKeyDown={(domEvent) => {
                      if (domEvent.key === 'Enter' || domEvent.key === ' ') {
                        domEvent.preventDefault()
                        handleMonthDayClick(day)
                      }
                    }}
                  >
                    <div className="ds-scheduler__month-cell-header">
                      <span className="ds-scheduler__month-day-number">
                        {day.getDate()}
                      </span>
                      <span
                        className={`ds-scheduler__month-day-count${
                          (summary?.totalAppointments ?? 0) === 0
                            ? ' ds-scheduler__month-day-count--empty'
                            : ''
                        }`}
                        aria-label={`${summary?.totalAppointments ?? 0} appointments`}
                      >
                        <i
                          className="mgc_group_3_fill ds-scheduler__month-day-count-icon"
                          aria-hidden="true"
                        />
                        <span>{summary?.totalAppointments ?? 0}</span>
                      </span>
                    </div>
                    <div className="ds-scheduler__month-providers">
                      {visibleProviders.map((provider) => {
                        const tokens = getScheduleCardColorTokens(
                          provider.color ?? 'pale-green',
                        )
                        return (
                          <span
                            key={`${dayKey}-${provider.providerName}`}
                            className="ds-scheduler__month-provider"
                            style={{
                              background: tokens.surface,
                              borderColor: tokens.border,
                              color: tokens.text,
                            }}
                          >
                            <span className="ds-scheduler__month-provider-name">
                              {provider.providerName}
                            </span>
                            <span className="ds-scheduler__month-provider-count">
                              {provider.count}
                            </span>
                          </span>
                        )
                      })}
                      {overflowCount > 0 ? (
                        <button
                          type="button"
                          className={`ds-scheduler__month-more${
                            moreOpen ? ' ds-scheduler__month-more--open' : ''
                          }`}
                          aria-expanded={moreOpen}
                          aria-haspopup="listbox"
                          onClick={(domEvent) => {
                            domEvent.stopPropagation()
                            if (moreOpen) {
                              setMonthMoreOpen(null)
                              return
                            }
                            openMonthMore(
                              dayKey,
                              remainingProviders,
                              domEvent.currentTarget,
                            )
                          }}
                        >
                          +{overflowCount} more
                        </button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {monthMoreOpen
        ? createPortal(
            <>
              <button
                type="button"
                className="ds-scheduler__month-more-backdrop"
                aria-label="Close more providers"
                onClick={() => setMonthMoreOpen(null)}
              />
              <div
                className={`ds-scheduler__month-more-menu${
                  darkMode ? ' ds-scheduler__month-more-menu--dark' : ''
                }`}
                style={{
                  top: monthMoreOpen.top,
                  left: monthMoreOpen.left,
                }}
                role="listbox"
                aria-label="More providers"
                onClick={(domEvent) => domEvent.stopPropagation()}
              >
                {monthMoreOpen.providers.map((provider) => {
                  const tokens = getScheduleCardColorTokens(
                    provider.color ?? 'pale-green',
                  )
                  return (
                    <div
                      key={provider.providerName}
                      className="ds-scheduler__month-provider"
                      role="option"
                      aria-selected={false}
                      style={{
                        background: tokens.surface,
                        borderColor: tokens.border,
                        color: tokens.text,
                      }}
                    >
                      <span className="ds-scheduler__month-provider-name">
                        {provider.providerName}
                      </span>
                      <span className="ds-scheduler__month-provider-count">
                        {provider.count}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>,
            document.body,
          )
        : null}

      {selectedEvent?.info && infoAnchor
        ? createPortal(
            <SchedulerAppointmentInfoPanel
              info={selectedEvent.info}
              darkMode={darkMode}
              style={{
                position: 'fixed',
                top: infoAnchor.top,
                left: infoAnchor.left,
              }}
              onClose={handleCloseInfo}
              onMouseEnter={clearInfoCloseTimeout}
              onMouseLeave={scheduleCloseInfo}
            />,
            document.body,
          )
        : null}

      {contextMenu ? (
        <SchedulerContextMenu
          event={contextMenu.event}
          top={contextMenu.top}
          left={contextMenu.left}
          darkMode={darkMode}
          onClose={() => setContextMenu(null)}
          onAction={(action) => handleContextAction(contextMenu.event, action)}
        />
      ) : null}

      <Modal
        open={Boolean(pendingMove)}
        onClose={closeMoveModal}
        title="Move appointment?"
        size="sm"
        variant="default"
        showClose
        darkMode={darkMode}
        primaryLabel="Move with provider"
        secondaryLabel="Switch provider"
        secondaryRightIcon={
          <IconChevronDown size={16} stroke={2} aria-hidden="true" />
        }
        onPrimaryClick={() =>
          applyPendingMove(moveProviderId || pendingMove?.event.providerId)
        }
        onSecondaryClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          setProviderMenu((current) =>
            current
              ? null
              : {
                  top: rect.top,
                  left: rect.left,
                  width: Math.max(rect.width, 180),
                },
          )
        }}
        footerStart={
          <Button
            variant="tertiary"
            size="sm"
            darkMode={darkMode}
            onClick={closeMoveModal}
          >
            Cancel
          </Button>
        }
      >
        {pendingMove ? (
          <div className="ds-scheduler__move">
            <p className="ds-scheduler__move-lead">
              You will be moving this appointment
            </p>
            <Search
              className="ds-scheduler__move-search"
              placeholder="Search"
              value={moveSearch}
              darkMode={darkMode}
              onChange={setMoveSearch}
              onClear={() => setMoveSearch('')}
            />
            <div className="ds-scheduler__move-card">
              <div className="ds-scheduler__move-side">
                <span className="ds-scheduler__move-label">From</span>
                <strong className="ds-scheduler__move-room">
                  {pendingMove.sourceColumnLabel}
                </strong>
                <span className="ds-scheduler__move-provider">
                  {pendingMove.event.providerName ?? '—'}
                </span>
                <span className="ds-scheduler__move-meta">
                  {formatMoveMeta(pendingMove.event.start)}
                </span>
              </div>
              <span className="ds-scheduler__move-arrow" aria-hidden="true">
                <span className="ds-scheduler__move-arrow-inner">
                  <ArrowCircleRightIcon
                    className="ds-scheduler__move-arrow-icon"
                    width={16}
                    height={16}
                  />
                </span>
              </span>
              <div className="ds-scheduler__move-side ds-scheduler__move-side--to">
                <span className="ds-scheduler__move-label">To</span>
                <strong className="ds-scheduler__move-room">
                  {pendingMove.targetColumnLabel}
                </strong>
                <span className="ds-scheduler__move-provider">
                  {providers.find((provider) => provider.id === moveProviderId)
                    ?.name ??
                    pendingMove.event.providerName ??
                    '—'}
                </span>
                <span className="ds-scheduler__move-meta">
                  {formatMoveMeta(pendingMove.nextStart)}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      {providerMenu && pendingMove
        ? createPortal(
            <>
              <button
                type="button"
                className="ds-scheduler__provider-menu-backdrop"
                aria-label="Close provider menu"
                onClick={() => setProviderMenu(null)}
              />
              <div
                className={`ds-scheduler__provider-menu${
                  darkMode ? ' ds-scheduler__provider-menu--dark' : ''
                }`}
                style={{
                  top: providerMenu.top,
                  left: providerMenu.left,
                  width: providerMenu.width,
                  transform: 'translateY(calc(-100% - 8px))',
                }}
                role="listbox"
                aria-label="Switch provider"
              >
                {filteredMoveProviders.length === 0 ? (
                  <div className="ds-scheduler__provider-menu-empty">
                    No providers found
                  </div>
                ) : (
                  filteredMoveProviders.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      role="option"
                      aria-selected={moveProviderId === provider.id}
                      className={`ds-scheduler__provider-menu-item${
                        moveProviderId === provider.id
                          ? ' ds-scheduler__provider-menu-item--selected'
                          : ''
                      }`}
                      onClick={() => {
                        setMoveProviderId(provider.id)
                        setProviderMenu(null)
                      }}
                    >
                      {provider.name}
                    </button>
                  ))
                )}
              </div>
            </>,
            document.body,
          )
        : null}

      <Modal
        open={unavailableOpen}
        onClose={() => setUnavailableOpen(false)}
        title="Rescheduling not available"
        description={
          unavailableReason === 'past'
            ? 'Appointments cannot be moved to a past date or time.'
            : unavailableReason === 'occupied'
              ? 'This room already has an appointment scheduled at this time. Please select a different time slot or room.'
              : 'That time overlaps a closed or blocked hour.'
        }
        size="sm"
        variant="notice"
        iconTone="warning"
        darkMode={darkMode}
        primaryLabel="OK"
        onPrimaryClick={() => setUnavailableOpen(false)}
      />

      <Modal
        open={Boolean(detailEvent)}
        onClose={() => setDetailEvent(null)}
        title="Appointment details"
        size="md"
        darkMode={darkMode}
        primaryLabel="Close"
        onPrimaryClick={() => setDetailEvent(null)}
        secondaryLabel="Edit status"
        onSecondaryClick={() => {
          if (!detailEvent) return
          setContextMenu({
            event: detailEvent,
            top: window.innerHeight / 3,
            left: window.innerWidth / 2,
          })
          setDetailEvent(null)
        }}
      >
        {detailEvent ? (
          <div className="ds-scheduler__detail-grid">
            <div>
              <span>Patient</span>
              <strong>{detailEvent.patientName}</strong>
            </div>
            <div>
              <span>Provider</span>
              <strong>{detailEvent.providerName ?? '—'}</strong>
            </div>
            <div>
              <span>Room</span>
              <strong>
                {columns.find((column) => column.id === detailEvent.columnId)
                  ?.label ?? detailEvent.columnId}
              </strong>
            </div>
            <div>
              <span>Time</span>
              <strong>
                {formatEventTimeRange(detailEvent.start, detailEvent.end)}
              </strong>
            </div>
            <div>
              <span>Type</span>
              <strong>{detailEvent.appointmentType ?? '—'}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>{detailEvent.status ?? '—'}</strong>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(cancelEvent)}
        onClose={() => setCancelEvent(null)}
        title="Cancel appointment"
        description={
          cancelEvent
            ? `Cancel ${cancelEvent.patientName}'s appointment? (mock — removes from local list)`
            : undefined
        }
        size="sm"
        variant="confirmation"
        iconTone="danger"
        darkMode={darkMode}
        primaryDestructive
        primaryLabel="Cancel appointment"
        secondaryLabel="Keep"
        onPrimaryClick={() => {
          if (!cancelEvent) return
          commitEvents(events.filter((event) => event.id !== cancelEvent.id))
          setCancelEvent(null)
          handleCloseInfo()
        }}
        onSecondaryClick={() => setCancelEvent(null)}
      />

      <Modal
        open={Boolean(mockNotice)}
        onClose={() => setMockNotice(null)}
        title="Scheduler action"
        size="sm"
        darkMode={darkMode}
        description={mockNotice ?? undefined}
        primaryLabel="OK"
        onPrimaryClick={() => setMockNotice(null)}
      />

      <Modal
        open={setAppointmentOpen}
        onClose={() => {
          setSetAppointmentOpen(false)
          setSetAppointmentSlot(null)
        }}
        title="Set appointment"
        size="sm"
        darkMode={darkMode}
        description={
          setAppointmentSlot
            ? `${boardColumns.find((column) => column.id === setAppointmentSlot.columnId)?.label ?? setAppointmentSlot.columnId} · ${formatEventTimeRange(setAppointmentSlot.start, setAppointmentSlot.end)}`
            : 'Create a new appointment for the selected slot.'
        }
        primaryLabel="Continue"
        secondaryLabel="Cancel"
        onPrimaryClick={() => {
          setSetAppointmentOpen(false)
          setSetAppointmentSlot(null)
          setMockNotice(
            'Set appointment (mock in showcase — on /appointment?dsScheduler=1 this opens the live Schedule Appointment modal).',
          )
        }}
        onSecondaryClick={() => {
          setSetAppointmentOpen(false)
          setSetAppointmentSlot(null)
        }}
      />
    </div>
  )
}

export default Scheduler
